import type { EngineExports } from '@observant/shared';

/**
 * Imports provided to the wasm module. The M1 freestanding module imports
 * nothing; this is the seam where M2 will pass host callbacks (logging,
 * RNG seeds, time) once the engine needs them.
 */
export type EngineImports = WebAssembly.Imports;

/**
 * Fetch and instantiate `engine.wasm`, preferring streaming compilation.
 *
 * Uses `instantiateStreaming` when the server sends `application/wasm`
 * (Cloudflare static assets and Vite both do), and falls back to
 * array-buffer instantiation otherwise — older/misconfigured servers, or
 * `file://` contexts, throw on streaming.
 */
export async function instantiateEngine(
  wasmUrl: string,
  imports: EngineImports = {},
): Promise<{ exports: EngineExports; instance: WebAssembly.Instance }> {
  const response = await fetch(wasmUrl);
  if (!response.ok) {
    throw new Error(`engine: failed to fetch ${wasmUrl} (HTTP ${response.status})`);
  }

  let result: WebAssembly.WebAssemblyInstantiatedSource;
  if (typeof WebAssembly.instantiateStreaming === 'function') {
    try {
      result = await WebAssembly.instantiateStreaming(response, imports);
    } catch {
      // Streaming can fail on a wrong/missing MIME type; retry from bytes.
      const bytes = await (await fetch(wasmUrl)).arrayBuffer();
      result = await WebAssembly.instantiate(bytes, imports);
    }
  } else {
    const bytes = await response.arrayBuffer();
    result = await WebAssembly.instantiate(bytes, imports);
  }

  return {
    instance: result.instance,
    exports: result.instance.exports as unknown as EngineExports,
  };
}
